/* eslint-disable no-restricted-syntax */

import {
  ModelTreeNode,
  ModelTreeNodeDataOption,
  ParsedTreeNodeType,
} from '../data/modelOptionsTree';

export class TreeManipulation {

  public static findNodeInTree(
    treeNode : ModelTreeNode,
    treeNodeParent : ModelTreeNode | undefined,
    indexInParent: number | undefined,
    criteria : (elem : ModelTreeNode) => boolean,
  ) : {
      node : ModelTreeNode | undefined,
      parent : ModelTreeNode | undefined,
      indexInParent : number | undefined,
    } {

    // Check the node itself
    if (criteria(treeNode) === true) {
      return {
        node: treeNode,
        parent: treeNodeParent,
        indexInParent,
      };
    }

    // Call this function recursively for all the children
    let index = 0;
    for (const treeNodeChild of treeNode.children) {

      const result = this.findNodeInTree(
        treeNodeChild,
        treeNode,
        index,
        criteria,
      );

      if (result.node !== undefined) {
        return result;
      }

      index += 1;
    }

    return {
      node: undefined,
      parent: undefined,
      indexInParent: undefined,
    };
  }

  public static removeNodeFromTree(
    treeNode : ModelTreeNode,
    criteria : (elem : ModelTreeNode) => boolean,
  ) : number {

    let numChildrenRemoved = 0;

    // Call this function recursively for all the children

    for (let curChild = treeNode.children.length - 1; curChild >= 0; curChild -= 1) {

      // Grab the child
      const treeNodeChild = treeNode.children[curChild];

      // Delete it if passes the test
      if (criteria(treeNodeChild) === true) {
        treeNode.children.splice(curChild, 1);
        numChildrenRemoved += 1;
        // eslint-disable-next-line no-continue
        continue;
      }

      // If it does not pass the test, call this function recursively
      numChildrenRemoved += this.removeNodeFromTree(
        treeNodeChild,
        criteria,
      );
    }

    return numChildrenRemoved;
  }

  public static getGroupIDFromOptionNode(treeNode : ModelTreeNode) : number {

    if (treeNode.type !== ParsedTreeNodeType.Option) {
      throw new Error('Expected an Option node');
    }

    const nodeData = (treeNode.data as ModelTreeNodeDataOption);
    return nodeData.groupID;
  }

  public static findOptionNodeInTree(
    treeNode : ModelTreeNode,
    groupID : number,
    optionID : number,
  ) : {
      node : ModelTreeNode | undefined,
      parent : ModelTreeNode | undefined,
      indexInParent : number | undefined,
    } {
    return TreeManipulation.findNodeInTree(
      treeNode,
      undefined,
      undefined,
      (node) => {
        if (node.type !== ParsedTreeNodeType.Option) {
          return false;
        }

        if (this.getGroupIDFromOptionNode(node) !== groupID) {
          return false;
        }

        if (node.id !== optionID) {
          return false;
        }

        return true;
      },
    );
  }

  public static trimEmptyContainers(treeNode : ModelTreeNode) : void {

    // Call this function recursively for all the children
    for (let curChild = treeNode.children.length - 1; curChild >= 0; curChild -= 1) {

      // Grab the child
      const treeNodeChild = treeNode.children[curChild];

      // If it is a container, check if it is empty
      if (treeNodeChild.type === ParsedTreeNodeType.Domain
          || treeNodeChild.type === ParsedTreeNodeType.DomainGroup
          || treeNodeChild.type === ParsedTreeNodeType.Group
          || treeNodeChild.type === ParsedTreeNodeType.Folder
      ) {
        if (treeNodeChild.children.length === 0) {
          // Remove this child
          treeNode.children.splice(curChild, 1);
          // eslint-disable-next-line no-continue
          continue;
        }
      }

      // If it is not removed, call this function on this child
      this.trimEmptyContainers(treeNodeChild);
    }
  }

  public static removeSingleFoldersFromGroups(treeNode : ModelTreeNode) : void {

    // If we have a node that is a Group with a single child,
    // and that child is a Folder, we should remove the Folder,
    // and replace it with its children
    if (treeNode.type === ParsedTreeNodeType.Group
      && treeNode.children.length === 1
      && treeNode.children[0].type === ParsedTreeNodeType.Folder
    ) {
      // eslint-disable-next-line no-param-reassign
      treeNode.children = treeNode.children[0].children;
    }

    // Call this function recursively for all the children
    for (const child of treeNode.children) {
      this.removeSingleFoldersFromGroups(child);
    }
  }

  public static filterNodes(
    treeNode : ModelTreeNode,
    criteria : (elem : ModelTreeNode) => boolean,
  ) : ModelTreeNode[] {

    let results : ModelTreeNode[] = [];

    // Check the node itself
    if (criteria(treeNode) === true) {
      results.push(treeNode);
    }

    // Check the children
    treeNode.children.forEach((child) => {
      results = [...results, ...this.filterNodes(child, criteria)];
    });

    return results;
  }

  public static logTreeGroup = (node : ModelTreeNode, level : number) => {

    // eslint-disable-next-line no-console
    console.groupCollapsed('Tree');
    this.logTree(node, level);
    // eslint-disable-next-line no-console
    console.groupEnd();
  };

  public static logTree = (node : ModelTreeNode, level : number) => {
    // eslint-disable-next-line no-console
    console.log(' '.repeat(level * 2), node.id, '|', node.name, '|', node.type, '|', node.path);
    node.children.forEach((child) => {
      this.logTree(child, level + 1);
    });
  };
}
